import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import isEqual from 'lodash.isequal';
import { Dispatch, Fragment, memo, ReactElement } from 'react';
import { Marker, PointLike, Popup, PopupProps, useMap } from 'react-map-gl';
import styled, { css } from 'styled-components';
import Supercluster, { PointFeature } from 'supercluster';
import { ZOOM_MAX } from '../constants/map';
import { SearchHitFragment } from '../generated/graphql';
import { HitProperties, Properties } from '../hooks/search/usePoints';
import { HitMeta } from './SearchHit';
import RoundButton from '@oberoninternal/travelbase-ds/components/action/RoundButton';
import Caret from './svg/Caret';
import Accommodation from './svg/Accommodation';

interface MapClustersProps {
    clusters: PointFeature<Properties>[];
    supercluster?: Supercluster;
    renderSearchHit: (hit: SearchHitFragment) => ReactElement;
    activeId: Maybe<string>;
    setActiveId: Dispatch<string | null>;
}

const SearchMapClusters = ({ clusters, supercluster, renderSearchHit, activeId, setActiveId }: MapClustersProps) => {
    const map = useMap();
    return (
        <>
            {clusters.map(cluster => {
                const props = cluster.properties;
                const [longitude, latitude] = cluster.geometry.coordinates;
                const popupProps: PopupProps = {
                    longitude,
                    latitude,
                    closeButton: false,
                    closeOnClick: false,
                    offset: [0, -25] as PointLike,
                };

                if (props.cluster) {
                    const { cluster_id: clusterId, point_count: pointCount } = props;
                    const leaves = supercluster?.getLeaves(clusterId, Infinity) ?? [];
                    const activeLeaf = leaves.find(
                        (leaf): leaf is PointFeature<HitProperties> => !leaf.properties.cluster && leaf.id === activeId
                    );

                    const index = activeLeaf ? leaves.indexOf(activeLeaf) : -1;
                    return (
                        <Fragment key={`cluster-${clusterId}`}>
                            <div className={activeLeaf ? 'active-marker' : undefined}>
                                <Marker key={`cluster-${clusterId}`} longitude={longitude} latitude={latitude}>
                                    <MarkerContainer
                                        className={activeLeaf ? 'active' : undefined}
                                        onClick={() => {
                                            if (supercluster) {
                                                const expansionZoom = Math.min(
                                                    supercluster.getClusterExpansionZoom(clusterId),
                                                    ZOOM_MAX
                                                );
                                                if (expansionZoom === ZOOM_MAX) {
                                                    setActiveId((leaves?.[0].id as string) ?? null);
                                                } else {
                                                    setActiveId(null);
                                                    // zoom in closer
                                                    map.current?.fitBounds(
                                                        [
                                                            [longitude, latitude],
                                                            [longitude, latitude],
                                                        ],
                                                        {
                                                            padding: 200,
                                                            zoom: expansionZoom,
                                                        }
                                                    );
                                                }
                                            }
                                        }}
                                    >
                                        <ClusterLabel>{pointCount}</ClusterLabel>
                                    </MarkerContainer>
                                </Marker>
                                {activeLeaf?.properties.hit && (
                                    <Popup {...popupProps}>
                                        <ClusterBalloon active={!!activeLeaf}>
                                            {renderSearchHit(activeLeaf.properties.hit)}
                                            <HitNavigation>
                                                <Label style={{ flex: 1 }}>{`${index + 1} / ${leaves.length}`}</Label>
                                                <RoundButton
                                                    variant="outline"
                                                    size="small"
                                                    onClick={() => {
                                                        setActiveId((leaves[index - 1].id as string) ?? null);
                                                    }}
                                                    disabled={index - 1 < 0}
                                                >
                                                    <Caret width={12} style={{ transform: 'rotate(90deg)' }} />
                                                </RoundButton>
                                                <RoundButton
                                                    variant="outline"
                                                    size="small"
                                                    onClick={() => {
                                                        setActiveId((leaves[index + 1].id as string) ?? null);
                                                    }}
                                                    disabled={index + 1 >= leaves.length}
                                                >
                                                    <Caret width={12} style={{ transform: 'rotate(-90deg)' }} />
                                                </RoundButton>
                                            </HitNavigation>
                                        </ClusterBalloon>
                                    </Popup>
                                )}
                            </div>
                        </Fragment>
                    );
                }

                const { hit } = props;
                const isActive = activeId === hit.rentalUnit.id;
                return (
                    <Fragment key={`${hit.rentalUnit.id}-${activeId}`}>
                        {hit && (
                            <Marker longitude={longitude} latitude={latitude}>
                                <MarkerContainer
                                    className={activeId === hit.rentalUnit.id ? 'active' : undefined}
                                    onClick={() => setActiveId(!isActive ? hit.rentalUnit.id : null)}
                                >
                                    <Accommodation />
                                </MarkerContainer>
                            </Marker>
                        )}

                        {isActive && (
                            <Popup {...popupProps}>
                                <HitBalloon active>{renderSearchHit(hit)}</HitBalloon>
                            </Popup>
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};

export default memo(SearchMapClusters, (prev, next) => {
    // pick a few properties from the props to make the comparison less expensive
    const { clusters: prevClusters, supercluster: nextSupercluster, ...prevProps } = prev;
    const { clusters: nextClusters, supercluster: prevSupercluster, ...nextProps } = next;

    return isEqual(
        {
            clusters: prevClusters.map(cluster => cluster.id),
            supercluster: !!nextSupercluster,
            ...prevProps,
        },
        {
            clusters: nextClusters.map(cluster => cluster.id),
            supercluster: !!prevSupercluster,
            ...nextProps,
        }
    );
});

const MarkerContainer = styled.div`
    background: ${({ theme }) => theme.colors.neutral[0]};
    box-shadow: 0 10px 19px 0 rgba(12, 59, 124, 0.1);
    width: var(--marker-diameter);
    height: var(--marker-diameter);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    cursor: pointer;

    :hover,
    &.active {
        background: ${({ theme }) => theme.colors.secondary[30]};
        color: ${({ theme }) => theme.colors.neutral[0]};
    }
`;

const HitNavigation = styled.div`
    padding: 1.6rem;
    margin-top: -0.6rem;
    z-index: 2;
    position: relative;
    gap: 0.6rem;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.colors.neutral[10]};
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;

    button {
        background: ${({ theme }) => theme.colors.neutral[0]};
    }
`;

const HitBalloon = styled.div<{ active: boolean }>`
    background: ${({ theme }) => theme.colors.neutral[0]};
    border-radius: 1rem;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.07);
    width: 28rem;
    position: relative;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 35rem;
    }

    ${HitMeta} {
        padding: 1.6rem;
        padding-top: inherit;
        background: ${({ theme }) => theme.colors.neutral[0]};
        z-index: 1;
        border-bottom-left-radius: 1rem;
        border-bottom-right-radius: 1rem;
    }

    img {
        border-top-left-radius: 1rem;
        border-top-right-radius: 1rem;
        pointer-events: none;
    }

    ${({ active }) =>
        active &&
        css`
            z-index: 1;
        `};
`;

const ClusterBalloon = styled(HitBalloon)`
    :after {
        background: ${({ theme }) => theme.colors.neutral[10]};
    }
`;

const ClusterLabel = styled.span`
    font-weight: bold;
    font-size: 1.7rem;
`;
